import type { ReactElement, ReactNode } from 'react';
import { ProgressBar } from '../../ds';

export type LessonTrack = 'kana' | 'vocab' | 'grammar' | 'listening' | 'checkpoint' | 'practice';

const stroke = (d: ReactElement, c: string, s: number, w = 2): ReactElement => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);

const ICON = {
  sparkles: (c: string, s: number) => stroke(<path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />, c, s),
  book: (c: string, s: number) =>
    stroke(
      <>
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5Z" />
        <path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20v3H6.5" />
      </>,
      c,
      s,
    ),
  volume: (c: string, s: number) =>
    stroke(
      <>
        <path d="M11 5 6 9H2v6h4l5 4z" />
        <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      </>,
      c,
      s,
    ),
  check: (c: string, s: number) => stroke(<path d="M20 6 9 17l-5-5" />, c, s, 3),
  repeat: (c: string, s: number) =>
    stroke(
      <>
        <path d="M17 1l4 4-4 4" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </>,
      c,
      s,
    ),
  close: (c: string, s: number) => stroke(<path d="M6 6l12 12M18 6L6 18" />, c, s),
  back: (c: string, s: number) => stroke(<path d="M15 18l-6-6 6-6" />, c, s),
};

const TRACK_META: Record<LessonTrack, { label: string; icon: (c: string, s: number) => ReactElement }> = {
  kana: { label: 'Nền tảng', icon: ICON.sparkles },
  vocab: { label: 'Từ vựng', icon: ICON.book },
  grammar: { label: 'Ngữ pháp', icon: ICON.book },
  listening: { label: 'Nghe hiểu', icon: ICON.volume },
  checkpoint: { label: 'Kiểm tra', icon: ICON.check },
  practice: { label: 'Luyện tập', icon: ICON.repeat },
};

export function TrackChip({ track }: { track?: LessonTrack }) {
  const meta = TRACK_META[track ?? 'kana'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(0,91,150,0.09)', borderRadius: 999, padding: '3px 9px', flexShrink: 0 }}>
      {meta.icon('var(--cta-default)', 12)}
      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, color: 'var(--cta-default)', whiteSpace: 'nowrap' }}>{meta.label}</span>
    </span>
  );
}

export interface LessonCtx {
  track?: LessonTrack;
  unit?: string;
  lesson?: string;
  step?: number;
  total?: number;
}

/** Header dùng chung cho mọi màn hình trong một bài học (progress + track chip). */
export function LessonHeader({ onBack, useX = true, ctx, right }: { onBack: () => void; useX?: boolean; ctx: LessonCtx; right?: ReactNode }) {
  const step = ctx.step ?? 1;
  const total = Math.max(1, ctx.total ?? 1);
  const subtitle = [ctx.unit, ctx.lesson].filter(Boolean).join(' · ');

  return (
    <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-page)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          onClick={onBack}
          aria-label={useX ? 'Đóng' : 'Quay lại'}
          style={{ width: 44, height: 44, marginLeft: -10, border: 'none', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          {useX ? ICON.close('var(--ink)', 24) : ICON.back('var(--ink)', 24)}
        </button>
        <div style={{ flex: 1 }}>
          <ProgressBar value={step} max={total} />
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', minWidth: 30, textAlign: 'right' }}>{`${step}/${total}`}</div>
        {right}
      </div>
      {subtitle && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingLeft: 34, minWidth: 0 }}>
          <TrackChip track={ctx.track} />
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</span>
        </div>
      )}
    </div>
  );
}
